import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, catchError, finalize, map, of, shareReplay, tap } from 'rxjs';
import { CustomerContextResult, CustomerTerminologyTerm } from '../models/customer-context';
import { environment } from 'src/environments/environment';

@Injectable({
  providedIn: 'root'
})
export class CustomerContextService {
  private readonly systemTerms: Record<string, CustomerTerminologyTerm> = {
    audit: { Singular: 'Audit', Plural: 'Audits' }
  };
  private readonly contextSubject = new BehaviorSubject<CustomerContextResult>(this.defaultContext());
  private request$: Observable<CustomerContextResult> | null = null;
  private loaded = false;

  readonly context$ = this.contextSubject.asObservable();

  constructor(private httpClient: HttpClient) { }

  load(): Observable<CustomerContextResult> {
    if (this.loaded) {
      return of(this.contextSubject.value);
    }
    if (this.request$) {
      return this.request$;
    }

    const options = {
      headers: new HttpHeaders({ 'Content-Type': 'application/json' }),
      withCredentials: true
    };

    this.request$ = this.httpClient
      .get<CustomerContextResult>(`${environment.apiUrl}userauth/context`, options)
      .pipe(
        map(context => this.normalise(context)),
        catchError(() => of(this.defaultContext())),
        tap(context => {
          this.contextSubject.next(context);
          this.loaded = true;
        }),
        finalize(() => this.request$ = null),
        shareReplay({ bufferSize: 1, refCount: false })
      );

    return this.request$;
  }

  reset(): void {
    this.loaded = false;
    this.request$ = null;
    this.contextSubject.next(this.defaultContext());
  }

  term(termKey: string, plural = false): string {
    const key = termKey.trim().toLowerCase();
    const term = this.contextSubject.value.Terminology[key] ?? this.systemTerms[key];
    if (!term) {
      return termKey;
    }
    return plural ? term.Plural : term.Singular;
  }

  displayText(value: string | null | undefined): string {
    if (!value) {
      return value ?? '';
    }

    const protectedBrand: string[] = [];
    const text = value.replace(/Care Audits/gi, match => {
      protectedBrand.push(match);
      return `__CARE_AUDITS_BRAND_${protectedBrand.length - 1}__`;
    });

    return text
      .replace(/\bAudits\b/gi, match => this.matchCase(this.term('audit', true), match))
      .replace(/\bAudit\b/gi, match => this.matchCase(this.term('audit'), match))
      .replace(/__CARE_AUDITS_BRAND_(\d+)__/g, (_match, index) => protectedBrand[Number(index)]);
  }

  private matchCase(replacement: string, source: string): string {
    if (source === source.toUpperCase()) {
      return replacement.toUpperCase();
    }
    if (source === source.toLowerCase()) {
      return replacement.toLowerCase();
    }
    return replacement;
  }

  private normalise(context: CustomerContextResult | null | undefined): CustomerContextResult {
    const terminology = { ...this.systemTerms };
    if (context && !context.IsError && context.Terminology) {
      Object.entries(context.Terminology).forEach(([rawKey, rawTerm]) => {
        const key = rawKey.trim().toLowerCase();
        const singular = rawTerm?.Singular?.trim();
        const plural = rawTerm?.Plural?.trim();
        if (key && singular && plural) {
          terminology[key] = { Singular: singular, Plural: plural };
        }
      });
    }

    return {
      IsError: false,
      ErrorMessage: '',
      ErrorCode: '',
      CustomerID: context?.CustomerID ?? -1,
      Terminology: terminology
    };
  }

  private defaultContext(): CustomerContextResult {
    return {
      IsError: false,
      ErrorMessage: '',
      ErrorCode: '',
      CustomerID: -1,
      Terminology: { ...this.systemTerms }
    };
  }
}

